import { IGlobals } from '../interfaces'
import {
  RECOMMENDATION_CAROUSEL_LIMIT,
  RECOMMENDATION_LIST_LIMIT,
} from '../consts/defaultConfigValues/defaultConstants'

/**
 * The recommendation limits a storefront can retune, and what happens when it
 * gets one wrong.
 *
 * Every one of these values is sent as the GraphQL `$limit: Int`, and nothing
 * between the resolved prop and `getBuyAgain({ variables })` coerces it. That
 * makes the failure modes unusually quiet:
 *
 * - a quoted `"5"` is rejected at variable coercion, so the whole query fails
 *   and the carousel stays hidden — indistinguishable from a shopper with no
 *   purchase history;
 * - `0` is a *legal* `Int`, so the query succeeds, returns nothing, and every
 *   diagnostic in the system reports a healthy fetch;
 * - `null` is *not* dropped from the request — Apollo's `compact` strips only
 *   `undefined` — so it reaches the server as an explicit `null` rather than
 *   as an absent argument, and the server's default does not stand in for it.
 *
 * These read `unknown` rather than the declared types on purpose: the config
 * is remote JSON cast to `IConfig`, so at runtime these values can be
 * anything. Same reasoning as `resolvePackageSort`.
 *
 * `isUsableLimit` is also the predicate behind `utils/prefillCartLimit.ts`.
 */
export const isUsableLimit = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0

/**
 * The limit to actually use: the authored value when it can be sent, and the
 * shipped default when it cannot.
 *
 * Falling back rather than dropping the key matters — an absent `limit` lets
 * the server apply a default nobody in this repo controls, which is a second
 * silent divergence rather than a fix for the first.
 */
export const resolveRecommendationLimit = (
  value: unknown,
  fallback: number,
): number => (isUsableLimit(value) ? value : fallback)

/**
 * `globals` with both productized limits replaced by something sendable.
 *
 * **Must be applied before anything resolves a `{{config.globals...}}` binding
 * against this config.** `processContainers` eagerly bakes those bindings into
 * component props, so a value corrected after that point is corrected only on
 * the object `processConfig` returns — which nothing reads for this purpose —
 * while the malformed one travels on to the query. That mistake shipped once
 * and looked exactly like working code.
 *
 * Returns a new object rather than mutating: when the incoming config has no
 * `globals` key, `deepMerge` hands back `defaultConfig.globals` **by
 * reference**, and mutating it would poison the module-level singleton for
 * every later config load.
 */
export const withResolvedRecommendationLimits = (
  globals: IGlobals,
): IGlobals => {
  const recommendedProducts = globals?.recommendedProducts

  return {
    ...globals,
    recommendedProducts: {
      ...recommendedProducts,
      carouselLimit: resolveRecommendationLimit(
        recommendedProducts?.carouselLimit,
        RECOMMENDATION_CAROUSEL_LIMIT,
      ),
      listLimit: resolveRecommendationLimit(
        recommendedProducts?.listLimit,
        RECOMMENDATION_LIST_LIMIT,
      ),
    },
  }
}

/**
 * True when a storefront authored a limit that cannot be sent as an `Int`.
 *
 * **Feed this the authored config, not a processed one.** `processConfig`
 * corrects these values, so a caller that checks its output can never see a
 * bad one and the report can never fire. This is where it differs from
 * `isHidePricesMisconfigured` and `isPackageSortMisconfigured`, which are
 * safely fed processed config only because nothing rewrites what they read.
 *
 * Only the two productized keys are checked. `allRecommendations` and
 * `widgetHome` have the same hazard, but they are long-standing and a
 * storefront may be relying on whatever they currently do; changing that is a
 * behaviour change rather than a diagnostic.
 */
export const isRecommendationLimitsMisconfigured = (
  recommendedProducts?: Partial<IGlobals['recommendedProducts']>,
): boolean => {
  if (!recommendedProducts) return false

  const { carouselLimit, listLimit } = recommendedProducts

  return (
    (carouselLimit !== undefined && !isUsableLimit(carouselLimit)) ||
    (listLimit !== undefined && !isUsableLimit(listLimit))
  )
}
